'use server'

import { redirect } from 'next/navigation'
import bcrypt from 'bcryptjs'
import { randomBytes } from 'node:crypto'
import { execute, queryOne } from './db'
import { createSession, destroySession, getSessionUser } from './session'

/**
 * เข้าและออกจากระบบตาม FR-ADM-01
 * ข้อความแจ้งข้อผิดพลาดบอกสาเหตุกว้าง ๆ ไม่บอกว่าชื่อผู้ใช้มีอยู่จริงหรือไม่
 * เพราะจะกลายเป็นช่องให้เดาว่ามีบัญชีใดอยู่ในระบบบ้าง
 */
const MAX_FAILED = 5
const LOCK_MINUTES = 15

type Account = {
  id: number
  password_hash: string
  status: string
  failed_attempts: number
  locked_until: Date | null
}

export async function signIn(_prev: unknown, formData: FormData): Promise<{ error?: string }> {
  const identifier = String(formData.get('identifier') ?? '').trim()
  const password = String(formData.get('password') ?? '')
  if (!identifier || !password) return { error: 'กรอกชื่อผู้ใช้และรหัสผ่านให้ครบก่อน' }

  // รับได้ทั้งชื่อผู้ใช้และอีเมลตาม CR-05
  const acc = await queryOne<Account>(
    `SELECT id, password_hash, status, failed_attempts, locked_until
       FROM user_account WHERE username = ? OR email = ? LIMIT 1`,
    [identifier, identifier],
  )
  const fail = { error: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง ลองใหม่อีกครั้ง' }
  if (!acc) return fail

  if (acc.locked_until && acc.locked_until.getTime() > Date.now()) {
    return { error: `บัญชีถูกล็อกชั่วคราวเพราะกรอกรหัสผ่านผิดหลายครั้ง ลองใหม่อีกครั้งใน ${LOCK_MINUTES} นาที หรือติดต่อผู้ดูแลระบบ` }
  }
  if (acc.status !== 'active') {
    return { error: 'บัญชีนี้ถูกระงับการใช้งาน ติดต่อผู้ดูแลระบบเพื่อเปิดใช้งานอีกครั้ง' }
  }

  const ok = await bcrypt.compare(password, acc.password_hash)
  if (!ok) {
    const next = acc.failed_attempts + 1
    if (next >= MAX_FAILED) {
      await execute(
        `UPDATE user_account SET failed_attempts = 0, locked_until = DATE_ADD(UTC_TIMESTAMP(), INTERVAL ? MINUTE) WHERE id = ?`,
        [LOCK_MINUTES, acc.id],
      )
      return { error: `กรอกรหัสผ่านผิดครบ ${MAX_FAILED} ครั้ง บัญชีถูกล็อก ${LOCK_MINUTES} นาที` }
    }
    await execute(`UPDATE user_account SET failed_attempts = ? WHERE id = ?`, [next, acc.id])
    return fail
  }

  await execute(`UPDATE user_account SET failed_attempts = 0, locked_until = NULL WHERE id = ?`, [acc.id])
  // รหัสเซสชันเป็นค่าสุ่ม 32 ตัวอักษร ไม่ใช่เลขเรียง เพื่อไม่ให้เดารหัสของคนอื่นได้
  const sessionId = randomBytes(16).toString('hex')
  await execute(
    `INSERT INTO user_session (id, user_id, expires_at) VALUES (?, ?, DATE_ADD(UTC_TIMESTAMP(), INTERVAL 12 HOUR))`,
    [sessionId, acc.id],
  )
  await createSession(acc.id, sessionId)
  redirect('/')
}

export async function signOut() {
  const user = await getSessionUser()
  if (user) await execute(`DELETE FROM user_session WHERE user_id = ?`, [user.userId])
  await destroySession()
  redirect('/login')
}
